/**
 * The other dates the user has supplied, sent where the engine can act on them.
 *
 * `GET /matters/{matter}` takes only the filing date, so a deadline counted from registration
 * comes back refused with `registration_date_required` no matter what is on the header form.
 * `POST .../calculations` is the endpoint that takes every known date, so the panel asks it as
 * soon as the user supplies one and renders that answer instead.
 *
 * `receipt: false`, because a receipt pins an answer for a caller who is relying on it. Browsing
 * a filing on screen is not that, and writing one per keystroke would fill the table with drafts.
 */

import { useQuery } from '@tanstack/react-query';
import { calculate } from '~/api/validate';
import type { CalculationRequest, FilingLife } from '~/api/schema';
import type { DateParams } from '~/lib/routes';

/** The dates the caller supplies, beyond the filing date itself. */
const SUPPLIED = ['priority', 'publication', 'registration', 'grant'] as const;

export function suppliedDates(dates: DateParams): Partial<CalculationRequest> {
  const supplied: Partial<CalculationRequest> = {};
  for (const key of SUPPLIED) {
    const value = dates[key];
    if (value) supplied[key] = value;
  }
  return supplied;
}

export function hasSuppliedDates(dates: DateParams): boolean {
  return SUPPLIED.some((key) => Boolean(dates[key]));
}

export interface CalculationState {
  /** The obligations the supplied dates imply, or null while only the filing date is known. */
  life: FilingLife | null;
  pending: boolean;
  error: unknown;
}

export function useCalculation(
  code: string,
  matter: string,
  filing: string,
  dates: DateParams,
): CalculationState {
  const supplied = suppliedDates(dates);
  const enabled = code.length > 0 && filing.length > 0 && hasSuppliedDates(dates);

  const query = useQuery({
    queryKey: ['calculation', code, matter, filing, supplied],
    queryFn: ({ signal }) =>
      calculate(code, matter, { filing, receipt: false, ...supplied } as CalculationRequest, signal),
    enabled,
  });

  return {
    life: query.data?.life ?? null,
    pending: enabled && query.isPending,
    error: query.error,
  };
}
